import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, DatePicker, Input, InputNumber, Modal, Tag, message } from "antd";
import { DeleteOutlined, ThunderboltOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { taskScheduleApis } from "../../../api/TaskSchedule";

// Month-grid editor for a task's monthly production schedule. Pick a month
// range (rows regenerate, keeping already-entered quantities), type per-month
// quantities or auto-distribute the task quantity evenly, then Save — a full
// REPLACE of the task's schedule (empty grid = clear, confirmed first). A 2dp
// sum-vs-work-quantity mismatch shows as an amber, non-blocking warning.

const { RangePicker } = DatePicker;

const round4 = (n: number) => Math.round(n * 10000) / 10000;

export interface ScheduleTaskInfo {
  taskId: number;
  taskName?: string;
  workQuantity?: number | string | null;
  workUomName?: string;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
}

interface MonthRow {
  month: string; // 'YYYY-MM'
  qty: number | null;
}

const monthsBetween = (start: Dayjs, end: Dayjs): string[] => {
  const list: string[] = [];
  let cursor = start.startOf("month");
  const last = end.startOf("month");
  while (cursor.isBefore(last) || cursor.isSame(last, "month")) {
    list.push(cursor.format("YYYY-MM"));
    cursor = cursor.add(1, "month");
  }
  return list;
};

const TaskScheduleFormModal = ({
  open,
  onClose,
  task,
  initialMonths,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  task: ScheduleTaskInfo;
  initialMonths: Array<{ scheduleMonth: string; scheduledQty: string; varianceReason?: string }>;
  onSaved?: () => void;
}) => {
  const [rows, setRows] = useState<MonthRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [prefilled, setPrefilled] = useState(false);
  const [varianceReason, setVarianceReason] = useState("");

  const workQuantity = Number(task?.workQuantity) || 0;
  const uom = task?.workUomName || "";

  useEffect(() => {
    if (!open) return;
    setError("");
    setSaving(false);
    setPrefilled(false);
    if (initialMonths?.length) {
      setRows(
        initialMonths.map((m) => ({
          month: dayjs(m.scheduleMonth).format("YYYY-MM"),
          qty: Number(m.scheduledQty) || 0,
        }))
      );
      // keep the previously saved reason editable
      setVarianceReason(initialMonths.find((m) => m.varianceReason)?.varianceReason || "");
      return;
    }
    setVarianceReason("");
    // No schedule yet: prefill the month grid from the task's planned
    // execution window (quantities left empty — "Distribute evenly" is one
    // click away). Skipped when the dates are missing/invalid or the window
    // is absurdly long (bad data guard).
    const start = task?.plannedStartDate ? dayjs(task.plannedStartDate) : null;
    const end = task?.plannedEndDate ? dayjs(task.plannedEndDate) : null;
    if (
      start?.isValid() &&
      end?.isValid() &&
      !end.isBefore(start, "month") &&
      end.diff(start, "month") <= 60
    ) {
      setRows(monthsBetween(start, end).map((month) => ({ month, qty: null })));
      setPrefilled(true);
    } else {
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const range: [Dayjs, Dayjs] | null = useMemo(() => {
    if (!rows.length) return null;
    return [dayjs(`${rows[0].month}-01`), dayjs(`${rows[rows.length - 1].month}-01`)];
  }, [rows]);

  const onRangeChange = useCallback(
    (value: null | [Dayjs | null, Dayjs | null]) => {
      if (!value || !value[0] || !value[1]) {
        setRows([]);
        return;
      }
      const monthList = monthsBetween(value[0], value[1]);
      setRows((prev) => {
        const prevQty = new Map(prev.map((r) => [r.month, r.qty]));
        return monthList.map((month) => ({
          month,
          qty: prevQty.has(month) ? prevQty.get(month) ?? null : null,
        }));
      });
    },
    []
  );

  const distributeEvenly = useCallback(() => {
    setRows((prev) => {
      if (!prev.length || !workQuantity) return prev;
      const even = round4(workQuantity / prev.length);
      return prev.map((row, idx) => ({
        ...row,
        // remainder onto the last month so the total reconciles exactly
        qty:
          idx === prev.length - 1
            ? round4(workQuantity - even * (prev.length - 1))
            : even,
      }));
    });
  }, [workQuantity]);

  const total = useMemo(
    () => round4(rows.reduce((acc, row) => acc + (Number(row.qty) || 0), 0)),
    [rows]
  );
  // Differences within ±1 unit are rounding noise (task quantities are often
  // stored rounded while schedule totals are precise) — mirrors the backend
  // SCHEDULE_SUM_TOLERANCE. Beyond it: warning only, never a block.
  const SUM_TOLERANCE = 1;
  const delta = workQuantity > 0 ? Number((total - workQuantity).toFixed(2)) : 0;
  const mismatch = workQuantity > 0 && Math.abs(delta) > SUM_TOLERANCE;

  // Planned-window check at month granularity (mirrors the backend): months
  // before the planned start month or after the planned end month are flagged
  // amber and require a variance reason (e.g. "geology slip") before saving.
  const startMonth = task?.plannedStartDate && dayjs(task.plannedStartDate).isValid()
    ? dayjs(task.plannedStartDate).format("YYYY-MM")
    : null;
  const endMonth = task?.plannedEndDate && dayjs(task.plannedEndDate).isValid()
    ? dayjs(task.plannedEndDate).format("YYYY-MM")
    : null;
  const isOutOfWindow = useCallback(
    (month: string) =>
      Boolean((startMonth && month < startMonth) || (endMonth && month > endMonth)),
    [startMonth, endMonth]
  );
  const outOfWindowRows = useMemo(
    () => rows.filter((row) => isOutOfWindow(row.month)),
    [rows, isOutOfWindow]
  );
  const reasonMissing = outOfWindowRows.length > 0 && !varianceReason.trim();

  const doSave = useCallback(async () => {
    try {
      setSaving(true);
      setError("");
      const res = await taskScheduleApis.save(
        Number(task.taskId),
        rows.map((row) => ({
          scheduleMonth: `${row.month}-01`,
          scheduledQty: String(round4(Number(row.qty) || 0)),
          ...(isOutOfWindow(row.month) && varianceReason.trim()
            ? { varianceReason: varianceReason.trim() }
            : {}),
        }))
      );
      if (res?.status === true) {
        message.success(res?.data?.message || "Task schedule saved");
        if (res?.data?.warning) message.warning(res.data.warning, 6);
        onSaved?.();
        onClose();
      } else {
        setError(res?.message || "Failed to save the schedule");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to save the schedule");
    } finally {
      setSaving(false);
    }
  }, [task?.taskId, rows, isOutOfWindow, varianceReason, onSaved, onClose]);

  const onSaveClick = useCallback(() => {
    if (reasonMissing) {
      setError(
        "Some months fall outside the task's planned dates — enter the reason (e.g. geology slip) before saving."
      );
      return;
    }
    if (!rows.length) {
      Modal.confirm({
        title: "Clear this task's schedule?",
        content:
          "No months are set — saving will remove the task's monthly schedule entirely.",
        okText: "Clear Schedule",
        okButtonProps: { danger: true },
        onOk: doSave,
      });
      return;
    }
    doSave();
  }, [rows.length, reasonMissing, doSave]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      title={
        <span style={{ color: "#022c57" }}>
          Monthly Production Schedule
          {task?.taskName ? (
            <span style={{ color: "#8094ae", fontWeight: 400 }}>
              {" "}
              — {task.taskName}
            </span>
          ) : null}
        </span>
      }
      destroyOnClose
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <RangePicker
          picker="month"
          format="MMM YYYY"
          value={range}
          onChange={onRangeChange}
          allowClear
        />
        <Button
          icon={<ThunderboltOutlined />}
          onClick={distributeEvenly}
          disabled={!rows.length || !workQuantity}
        >
          Distribute evenly
        </Button>
      </div>

      {workQuantity > 0 && (
        <div style={{ color: "#526484", fontSize: 12, marginBottom: 8 }}>
          Task quantity: <b style={{ color: "#022c57" }}>{workQuantity}</b>
          {uom ? ` ${uom}` : ""} — spread it across the months below.
        </div>
      )}

      {prefilled && rows.length > 0 && (
        <div style={{ color: "#8094ae", fontSize: 12, marginBottom: 8 }}>
          Months prefilled from the task's planned dates (
          {dayjs(`${rows[0].month}-01`).format("MMM YYYY")} –{" "}
          {dayjs(`${rows[rows.length - 1].month}-01`).format("MMM YYYY")}) —
          adjust the range or remove months as needed.
        </div>
      )}

      {!rows.length ? (
        <div
          style={{
            border: "1px dashed #dbdfea",
            borderRadius: 6,
            padding: "18px 12px",
            textAlign: "center",
            color: "#8094ae",
            marginBottom: 12,
          }}
        >
          Pick a start and end month to build the schedule grid.
        </div>
      ) : (
        <div
          style={{
            border: "1px solid #dbdfea",
            borderRadius: 6,
            marginBottom: 12,
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          {rows.map((row, idx) => (
            <div
              key={row.month}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "6px 12px",
                borderTop: idx === 0 ? "none" : "1px solid #f0f2f7",
              }}
            >
              <span style={{ width: 150, color: "#022c57", fontWeight: 500 }}>
                {dayjs(`${row.month}-01`).format("MMM YYYY")}
                {isOutOfWindow(row.month) && (
                  <Tag color="gold" style={{ marginLeft: 6, fontSize: 10, lineHeight: "16px" }}>
                    outside plan
                  </Tag>
                )}
              </span>
              <InputNumber
                min={0}
                style={{ flex: 1 }}
                placeholder="0"
                value={row.qty as any}
                onChange={(value) =>
                  setRows((prev) =>
                    prev.map((r) =>
                      r.month === row.month ? { ...r, qty: value === null ? null : Number(value) } : r
                    )
                  )
                }
              />
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() =>
                  setRows((prev) => prev.filter((r) => r.month !== row.month))
                }
              />
            </div>
          ))}
        </div>
      )}

      {outOfWindowRows.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: "#b58a1f", fontSize: 12, marginBottom: 6 }}>
            {outOfWindowRows.length} month
            {outOfWindowRows.length === 1 ? " falls" : "s fall"} outside the
            task's planned dates
            {startMonth || endMonth ? (
              <>
                {" "}
                ({startMonth ? dayjs(`${startMonth}-01`).format("MMM YYYY") : "…"} –{" "}
                {endMonth ? dayjs(`${endMonth}-01`).format("MMM YYYY") : "…"})
              </>
            ) : null}
            . Capture why:
          </div>
          <Input.TextArea
            value={varianceReason}
            onChange={(e) => setVarianceReason(e.target.value)}
            placeholder="Reason for scheduling outside the planned window — e.g. geology slip at face 2, re-forecast after flooding"
            maxLength={500}
            autoSize={{ minRows: 2, maxRows: 4 }}
            status={reasonMissing ? "warning" : undefined}
          />
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: "#f5f6fa",
          borderRadius: 6,
          marginBottom: 12,
        }}
      >
        <span style={{ color: "#526484" }}>
          Total scheduled:{" "}
          <b style={{ color: "#022c57" }}>
            {total}
            {uom ? ` ${uom}` : ""}
          </b>
          {workQuantity > 0 ? (
            <span style={{ color: "#8094ae" }}> of {workQuantity}</span>
          ) : null}
        </span>
        {workQuantity > 0 &&
          (mismatch ? (
            <Tag color="gold">
              Δ {delta > 0 ? "+" : ""}
              {delta}
            </Tag>
          ) : delta !== 0 ? (
            // within tolerance: no warning, but keep the exact delta visible
            <Tag color="green">
              Within tolerance (Δ {delta > 0 ? "+" : ""}
              {delta})
            </Tag>
          ) : (
            <Tag color="green">Matches task qty</Tag>
          ))}
      </div>

      {mismatch && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Scheduled total differs from the task quantity"
          description="You can still save — the mismatch is recorded as a warning, not an error."
        />
      )}

      {error && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} />
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="primary" loading={saving} onClick={onSaveClick}>
          Save Schedule
        </Button>
      </div>
    </Modal>
  );
};

export default TaskScheduleFormModal;
